import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

export interface WaypointFlythroughWaypoint {
  position: [number, number, number];
  look_at: [number, number, number];
}

interface WaypointFlythroughProps {
  viewerRef: React.MutableRefObject<ViewerLike | null>;
  isActive: boolean;
  waypoints: WaypointFlythroughWaypoint[];
  durationMs?: number;
  worldCoords?: boolean;
  onComplete?: () => void;
  onClose: () => void;
}

interface ViewerLike {
  camera?: THREE.Camera & { position: THREE.Vector3 };
  controls?: {
    movementSpeed?: number;
    lookSpeed?: number;
    updateLookDirection?: () => void;
  };
  maxClickToMoveDistance?: number;
  clickTransitioning?: boolean;
  transitioningCameraTarget?: boolean;
  nextCamera?: { copy: (camera: THREE.Camera) => void };
  previousCamera?: { copy: (camera: THREE.Camera) => void };
  renderer?: { domElement?: HTMLCanvasElement };
}

interface FrozenViewerState {
  movementSpeed: number;
  lookSpeed: number;
  maxClickToMoveDistance: number;
}

interface RecordedWalkthrough {
  url: string;
  filename: string;
}

type PlayState = 'idle' | 'playing' | 'complete';
type RecordingState = 'idle' | 'recording' | 'processing' | 'ready' | 'failed';

const DEFAULT_DURATION_MS = 20_000;
const RECORDING_FRAME_RATE = 30;
const ENU_TO_THREEJS = new THREE.Quaternion(0.5, 0.5, 0.5, -0.5);

function enuToWorld(enu: [number, number, number]): THREE.Vector3 {
  return new THREE.Vector3(enu[0], enu[1], enu[2]).applyQuaternion(ENU_TO_THREEJS);
}

function toWorld(coord: [number, number, number], worldCoords: boolean): THREE.Vector3 {
  return worldCoords ? new THREE.Vector3(coord[0], coord[1], coord[2]) : enuToWorld(coord);
}

function buildPositionPath(waypoints: WaypointFlythroughWaypoint[], worldCoords: boolean): THREE.CurvePath<THREE.Vector3> {
  // Use straight-line segments between stops — no curve overshoot, no degenerate lookAt.
  const padded = waypoints.length === 1 ? [waypoints[0], waypoints[0]] : waypoints;
  const pts = padded.map((wp) => toWorld(wp.position, worldCoords));
  const path = new THREE.CurvePath<THREE.Vector3>();
  for (let i = 0; i < pts.length - 1; i++) {
    path.add(new THREE.LineCurve3(pts[i], pts[i + 1]));
  }
  return path;
}

function freezeViewerControls(viewer: ViewerLike | null): FrozenViewerState {
  const movementSpeed        = viewer?.controls?.movementSpeed    ?? 5;
  const lookSpeed            = viewer?.controls?.lookSpeed        ?? 10;
  const maxClickToMoveDistance = viewer?.maxClickToMoveDistance   ?? 15;
  if (viewer?.controls) { viewer.controls.movementSpeed = 0; viewer.controls.lookSpeed = 0; }
  if (viewer) {
    viewer.maxClickToMoveDistance = 0;
    viewer.clickTransitioning = false;
    viewer.transitioningCameraTarget = false;
    if (viewer.nextCamera && viewer.camera)     viewer.nextCamera.copy(viewer.camera);
    if (viewer.previousCamera && viewer.camera) viewer.previousCamera.copy(viewer.camera);
  }
  return { movementSpeed, lookSpeed, maxClickToMoveDistance };
}

function restoreViewerControls(viewer: ViewerLike | null, state: FrozenViewerState | null) {
  if (!viewer || !state) return;
  if (viewer.controls) {
    viewer.controls.movementSpeed = state.movementSpeed;
    viewer.controls.lookSpeed     = state.lookSpeed;
  }
  viewer.maxClickToMoveDistance = state.maxClickToMoveDistance;
}

const WaypointFlythrough: React.FC<WaypointFlythroughProps> = ({
  viewerRef,
  isActive,
  waypoints,
  durationMs = DEFAULT_DURATION_MS,
  worldCoords = false,
  onComplete,
  onClose,
}) => {
  const [playState,       setPlayState]       = useState<PlayState>('idle');
  const [progress,        setProgress]        = useState(0);
  const [recordingState,  setRecordingState]  = useState<RecordingState>('idle');
  const [recordingError,  setRecordingError]  = useState<string | null>(null);
  const [recorded,        setRecorded]        = useState<RecordedWalkthrough | null>(null);

  const rafRef            = useRef<number>(0);
  const frozenStateRef    = useRef<FrozenViewerState | null>(null);
  const mediaRecorderRef  = useRef<MediaRecorder | null>(null);
  const streamRef         = useRef<MediaStream | null>(null);
  const chunksRef         = useRef<BlobPart[]>([]);
  const startTimeRef      = useRef<number>(0);
  const autoStartedRef    = useRef(false);
  const isActiveRef       = useRef(isActive);
  useEffect(() => { isActiveRef.current = isActive; }, [isActive]);

  // Keep a stable ref so the auto-start effect doesn't need startPlayback in its dep array
  const startPlaybackRef  = useRef(startPlayback);
  useEffect(() => { startPlaybackRef.current = startPlayback; }, [startPlayback]);

  const stopAnimation = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; }
  }, []);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') { recorder.stop(); return; }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const startRecording = useCallback((viewer: ViewerLike): boolean => {
    const canvas = viewer.renderer?.domElement;
    if (!canvas?.captureStream) {
      setRecordingState('failed');
      setRecordingError('This browser does not support canvas recording.');
      return false;
    }
    if (typeof MediaRecorder === 'undefined') {
      setRecordingState('failed');
      setRecordingError('This browser does not support video recording.');
      return false;
    }

    const stream = canvas.captureStream(RECORDING_FRAME_RATE);
    const mimeTypes = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm'];
    const mimeType = mimeTypes.find((t) => MediaRecorder.isTypeSupported(t));

    try {
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;
      streamRef.current = stream;
      chunksRef.current = [];
      setRecordingState('recording');
      setRecordingError(null);

      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };

      recorder.onerror = () => {
        setRecordingState('failed');
        setRecordingError('Recording failed while capturing the walkthrough.');
        stream.getTracks().forEach((t) => t.stop());
        mediaRecorderRef.current = null;
        streamRef.current = null;
      };

      recorder.onstop = () => {
        const chunks = chunksRef.current.slice();
        chunksRef.current = [];
        stream.getTracks().forEach((t) => t.stop());
        mediaRecorderRef.current = null;
        streamRef.current = null;

        if (!chunks.length) {
          setRecordingState('failed');
          setRecordingError('The walkthrough ended before a recording was saved.');
          return;
        }

        setRecordingState('processing');
        const blob = new Blob(chunks, { type: recorder.mimeType || 'video/webm' });
        const url = URL.createObjectURL(blob);
        const filename = `walkthrough-${Date.now()}.webm`;

        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setRecorded((current) => {
          if (current?.url) URL.revokeObjectURL(current.url);
          return { url, filename };
        });
        setRecordingState('ready');
      };

      recorder.start(250);
      return true;
    } catch (error) {
      stream.getTracks().forEach((t) => t.stop());
      mediaRecorderRef.current = null;
      streamRef.current = null;
      setRecordingState('failed');
      setRecordingError(error instanceof Error ? error.message : 'Recording could not be started.');
      return false;
    }
  }, []);

  const startPlayback = useCallback(
    (withRecording = false) => {
      if (!waypoints.length) return;
      const viewer = viewerRef.current;
      const camera = viewer?.camera;
      if (!viewer || !camera) {
        console.warn('[WaypointFlythrough] Viewer camera unavailable');
        return;
      }

      stopAnimation();
      if (withRecording) {
        if (!startRecording(viewer)) return;
      } else {
        setRecordingState('idle');
      }

      // Clamp all waypoints to the camera's current Y so we never dive inside splats.
      // Feature coordinates from the knowledge cache are ground-level; the camera needs
      // to stay at viewer eye height (wherever it is when the tour is triggered).
      const eyeY = camera.position.y;
      const safeWaypoints = waypoints.map((wp) => ({
        position: [wp.position[0], eyeY, wp.position[2]] as [number, number, number],
        look_at:  [wp.look_at[0],  eyeY, wp.look_at[2]]  as [number, number, number],
      }));
      console.log('[WaypointFlythrough] starting', { stops: safeWaypoints.length, eyeY, raw: waypoints, safe: safeWaypoints });

      // Prepend current camera position so playback starts from where the user is
      // rather than jumping immediately to waypoint[0].
      const camStart: WaypointFlythroughWaypoint = {
        position: [camera.position.x, eyeY, camera.position.z],
        look_at:  safeWaypoints[0].position,
      };
      const fullPath = [camStart, ...safeWaypoints];
      const positionPath = buildPositionPath(fullPath, worldCoords);

      const posVec  = new THREE.Vector3();
      const lookVec = new THREE.Vector3();
      let lastLookVec = new THREE.Vector3(camera.position.x, eyeY, camera.position.z + 1);

      setPlayState('playing');
      setProgress(0);
      startTimeRef.current = performance.now();

      const animate = (now: number) => {
        if (!isActiveRef.current) { stopAnimation(); return; }
        const av = viewerRef.current;
        const ac = av?.camera;
        if (!av || !ac) { stopAnimation(); return; }

        const t = Math.min((now - startTimeRef.current) / durationMs, 1);

        positionPath.getPoint(t, posVec);

        // Always look slightly ahead on the path — same curve, no separate lookAt curve.
        const lookT = Math.min(t + 0.015, 1.0);
        positionPath.getPoint(lookT, lookVec);

        // At the very end of the path lookVec ≈ posVec; reuse the last valid direction.
        if (posVec.distanceTo(lookVec) > 0.01) {
          lastLookVec.copy(lookVec);
        } else {
          lookVec.copy(lastLookVec);
        }

        if (!Number.isFinite(posVec.x) || !Number.isFinite(lookVec.x)) {
          console.warn('[WaypointFlythrough] NaN in path at t=', t, posVec, lookVec);
          stopAnimation();
          return;
        }

        ac.position.copy(posVec);
        ac.lookAt(lookVec);
        ac.updateMatrixWorld();
        // Do NOT call controls.updateLookDirection — it rewrites camera orientation
        // from the controls' frozen internal state, undoing our lookAt every frame.
        setProgress(t);

        if (t < 1) { rafRef.current = requestAnimationFrame(animate); return; }

        stopRecording();
        stopAnimation();
        setPlayState('complete');
        setProgress(1);
        restoreViewerControls(av, frozenStateRef.current);
        frozenStateRef.current = null;
        onComplete?.();
      };

      rafRef.current = requestAnimationFrame(animate);
    },
    [durationMs, onComplete, startRecording, stopAnimation, stopRecording, viewerRef, waypoints, worldCoords],
  );

  const handleStop = useCallback(() => {
    stopAnimation();
    stopRecording();
    restoreViewerControls(viewerRef.current, frozenStateRef.current);
    frozenStateRef.current = null;
    setPlayState('idle');
    setProgress(0);
  }, [stopAnimation, stopRecording, viewerRef]);

  const handlePlayAgain = useCallback(() => {
    handleStop();
    setTimeout(() => startPlayback(false), 100);
  }, [handleStop, startPlayback]);

  const handleRecordReplay = useCallback(() => {
    if (recorded?.url) { URL.revokeObjectURL(recorded.url); setRecorded(null); }
    setRecordingState('idle');
    setRecordingError(null);
    handleStop();
    setTimeout(() => startPlayback(true), 100);
  }, [handleStop, recorded?.url, startPlayback]);

  const handleDownloadRecording = useCallback(() => {
    if (!recorded?.url) return;
    const link = document.createElement('a');
    link.href = recorded.url;
    link.download = recorded.filename;
    link.rel = 'noopener';
    link.click();
  }, [recorded]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!isActive) {
      stopAnimation();
      stopRecording();
      restoreViewerControls(viewer, frozenStateRef.current);
      frozenStateRef.current = null;
      autoStartedRef.current = false;
      setPlayState('idle');
      setProgress(0);
      return;
    }
    if (!frozenStateRef.current && viewer) {
      frozenStateRef.current = freezeViewerControls(viewer);
    }
    return () => {
      stopAnimation();
      stopRecording();
      restoreViewerControls(viewer, frozenStateRef.current);
      frozenStateRef.current = null;
    };
  }, [isActive, stopAnimation, stopRecording, viewerRef]);

  // Auto-start playback when activated with waypoints
  useEffect(() => {
    if (!isActive || autoStartedRef.current || !waypoints.length) return;
    autoStartedRef.current = true;
    startPlaybackRef.current(false);
  }, [isActive, waypoints]);

  if (!isActive) return null;

  const progressPct   = Math.round(progress * 100);
  const isRecording   = recordingState === 'recording' || recordingState === 'processing';

  const instructionText =
    playState === 'idle'     ? `${waypoints.length} stops · ${Math.round(durationMs / 1000)}s` :
    playState === 'playing'  ? 'Walkthrough in progress...' :
                               'Walkthrough complete.';

  return (
    <div
      className="fixed z-[1002] w-[300px] rounded-2xl px-4 py-4 text-white shadow-xl"
      style={{
        top: 76,
        right: 16,
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(10px) saturate(140%)',
        WebkitBackdropFilter: 'blur(10px) saturate(140%)',
        border: '1px solid rgba(255,255,255,0.12)',
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold tracking-wide">Walkthrough</div>
          <div className="mt-1 text-xs text-white/70">{instructionText}</div>
        </div>
        <button
          onClick={onClose}
          className="text-white/70 hover:text-white transition-colors"
          aria-label="Close walkthrough"
        >
          ✕
        </button>
      </div>

      {/* Progress bar */}
      {playState === 'playing' && (
        <div className="mt-3">
          <div className="h-1 w-full overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full bg-[#0CB4E4]"
              style={{ width: `${progressPct}%`, transition: 'none' }}
            />
          </div>
          <div className="mt-1 text-right text-[11px] text-white/55">
            {isRecording ? '⏺ Recording… ' : ''}{progressPct}%
          </div>
        </div>
      )}

      {/* Recording error */}
      {recordingState === 'failed' && recordingError && (
        <div className="mt-3 rounded-xl border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs text-red-100">
          {recordingError}
        </div>
      )}

      {/* idle */}
      {playState === 'idle' && (
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            onClick={() => startPlayback(false)}
            disabled={!waypoints.length}
            className="rounded-xl bg-[#0CB4E4] px-3 py-2 text-sm font-semibold text-black transition-colors hover:bg-[#4FCBEE] disabled:opacity-50"
          >
            ▶ Play Again
          </button>
          <button
            onClick={() => startPlayback(true)}
            disabled={!waypoints.length}
            className="rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm text-white transition-colors hover:bg-white/10 disabled:opacity-50"
          >
            ⏺ Record &amp; Play
          </button>
        </div>
      )}

      {/* playing */}
      {playState === 'playing' && (
        <div className="mt-4">
          <button
            onClick={handleStop}
            className="w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm text-white transition-colors hover:bg-white/10"
          >
            ■ Stop
          </button>
        </div>
      )}

      {/* complete */}
      {playState === 'complete' && (
        <div className="mt-4 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={handlePlayAgain}
              className="rounded-xl bg-[#0CB4E4] px-3 py-2 text-sm font-semibold text-black transition-colors hover:bg-[#4FCBEE]"
            >
              ▶ Play Again
            </button>
            <button
              onClick={handleRecordReplay}
              className="rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm text-white transition-colors hover:bg-white/10"
            >
              ⏺ Record
            </button>
          </div>
          <button
            onClick={onClose}
            className="w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm text-white transition-colors hover:bg-white/10"
          >
            Close
          </button>
          {recordingState === 'ready' && recorded?.url && (
            <button
              onClick={handleDownloadRecording}
              className="w-full rounded-xl bg-[#0CB4E4] px-3 py-2 text-sm font-semibold text-black transition-colors hover:bg-[#4FCBEE]"
            >
              Download Recording
            </button>
          )}
          {recordingState === 'processing' && (
            <div className="text-center text-xs text-white/55">
              Preparing recording and starting download...
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WaypointFlythrough;
